import PropTypes from 'prop-types';
import React, { useEffect, useState } from 'react';
import { ArrowLeftIcon, ClockIcon } from '@heroicons/react/outline';
import Button from '../../../components/Button';
import Steps from '../../../common/components/Steps';
import { ReactComponent as TemplateIcon } from '../../ProjectWizardTemplates/components/brain.svg';
import ProjectWizardPublishControl from './ProjectWizardPublishControl';
import formatRelativeTimeAgo from './formatRelativeTimeAgo';

const ProjectWizardHeader = ({
  projectName,
  hasDraft,
  draftSavedAt,
  lastPublishedAt,
  onBack,
  steps,
  onPreparePublish,
  onClearDraft,
  onCommitDraft,
}) => {
  const [, setNow] = useState(Date.now());

  useEffect(() => {
    const intervalId = setInterval(() => {
      setNow(Date.now());
    }, 30000);
    return () => clearInterval(intervalId);
  }, []);

  const draftSavedLabel = formatRelativeTimeAgo(draftSavedAt);
  const lastPublishedLabel = formatRelativeTimeAgo(lastPublishedAt);

  return (
    <div className="px-6 pt-6 stack-2">
      <div className="p-6 rounded-md bg-pw2-header dark:bg-disabled">
        <div className="cluster-2 items-start">
          <Button
            data-testid="page-button-back"
            type="tertiary"
            icon={<ArrowLeftIcon />}
            onClick={onBack}
          />
          <TemplateIcon className="w-8 h-8 flex-shrink-0" />
          <div className="stack-2 flex-1 min-w-0">
            <div className="cluster-4 items-center justify-between gap-4">
              <div className="cluster-3 items-center min-w-0 flex-wrap">
                <h1
                  data-testid="page-header"
                  className="text-lg font-medium mt-0 truncate"
                >
                  {projectName}
                </h1>
                {hasDraft && (
                  <span
                    data-testid="label-changes-not-publish"
                    className="inline-flex items-center rounded-full border border-warning bg-warning-muted dark:bg-neutral-800 px-3 py-0.5 text-xs font-medium text-on-warning dark:text-warning whitespace-nowrap"
                  >
                    Changes not published
                  </span>
                )}
              </div>
              <div className="flex-shrink-0">
                <ProjectWizardPublishControl
                  hasDraft={hasDraft}
                  onPreparePublish={onPreparePublish}
                  onClearDraft={onClearDraft}
                  onCommitDraft={onCommitDraft}
                />
              </div>
            </div>
            {(hasDraft && draftSavedLabel) || lastPublishedLabel ? (
              <div className="cluster-4 items-center text-sm text-neutral-300 dark:text-neutral-300 flex-wrap">
                {lastPublishedLabel && (
                  <span data-testid="label-last-published-at">
                    Last published {lastPublishedLabel}
                  </span>
                )}
                {hasDraft && draftSavedLabel && lastPublishedLabel && (
                  <div className="w-px bg-divider h-4" aria-hidden="true" />
                )}
                {hasDraft && draftSavedLabel && (
                  <span
                    data-testid="label-draft-saved-at"
                    className="cluster-1 items-center"
                  >
                    <ClockIcon className="h-4 w-4" />
                    <span>Draft saved {draftSavedLabel}</span>
                  </span>
                )}
              </div>
            ) : null}
          </div>
        </div>
      </div>
      <div className="p-6 rounded-md bg-pw2-header dark:bg-disabled">
        <Steps steps={steps} />
      </div>
    </div>
  );
};

ProjectWizardHeader.propTypes = {
  projectName: PropTypes.string,
  hasDraft: PropTypes.bool,
  draftSavedAt: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.number,
    PropTypes.instanceOf(Date),
  ]),
  lastPublishedAt: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.number,
    PropTypes.instanceOf(Date),
  ]),
  onBack: PropTypes.func.isRequired,
  steps: PropTypes.arrayOf(PropTypes.object).isRequired,
  onPreparePublish: PropTypes.func.isRequired,
  onClearDraft: PropTypes.func.isRequired,
  onCommitDraft: PropTypes.func.isRequired,
};

ProjectWizardHeader.defaultProps = {
  projectName: '',
  hasDraft: false,
  draftSavedAt: null,
  lastPublishedAt: null,
};

export default ProjectWizardHeader;
